import { useUsers } from "@/contexts/users-provider";
import { useApi } from "@/hooks/useApi";
import { generateIdempotencyKey } from "@/hooks/use-idempotency-key";
import { useLicenseErrorToast } from "@/hooks/use-license-error-toast";
import { UserLicenseChangePreviewDTO } from "@shaarp/common";
import { useRef, useState } from "react";

type FlowStep = "idle" | "preflight" | "confirm";

type UserLicenseChangeState = {
  step: FlowStep;
  userId: string | null;
  targetPriceId: string | null;
  preview: UserLicenseChangePreviewDTO | null;
  isLoading: boolean;
};

/**
 * Hook orchestrating the Anthropic-inspired 2-step user license change flow.
 *
 * Flow:
 * 1. initiateChange(userId, targetPriceId)
 *    → calls preview API
 *    → if isSimpleSwap → applies directly (no modal)
 *    → else → opens preflight modal (step="preflight")
 * 2. advanceToConfirm() → moves to "confirm" step
 * 3. confirmChange() → calls confirm API, refreshes users, resets state
 */
export const useUserLicenseChange = (
  onInventoryRefetch?: () => Promise<void>
) => {
  const api = useApi();
  const showLicenseError = useLicenseErrorToast();
  const { apiFetchUsers } = useUsers();

  // Lazy init: generating inside useRef's argument would run on every render.
  const idempotencyKeyRef = useRef<string>("");
  if (!idempotencyKeyRef.current) {
    idempotencyKeyRef.current = generateIdempotencyKey();
  }

  const [state, setState] = useState<UserLicenseChangeState>({
    step: "idle",
    userId: null,
    targetPriceId: null,
    preview: null,
    isLoading: false,
  });

  const reset = () => {
    idempotencyKeyRef.current = generateIdempotencyKey();
    setState({
      step: "idle",
      userId: null,
      targetPriceId: null,
      preview: null,
      isLoading: false,
    });
  };

  const initiateChange = async (userId: string, targetPriceId: string) => {
    setState((prev) => ({ ...prev, isLoading: true, userId, targetPriceId }));

    try {
      const { data } = await api.post<UserLicenseChangePreviewDTO>(
        `/my/team/user/${userId}/license/change/preview`,
        { targetPriceId }
      );

      if (data.isSimpleSwap) {
        // Simple swap: apply directly without modals
        await api.post(
          `/my/team/user/${userId}/license/change`,
          { targetPriceId, confirmed: false },
          { headers: { "Idempotency-Key": idempotencyKeyRef.current } },
        );
        await apiFetchUsers({ force: true });
        if (onInventoryRefetch) await onInventoryRefetch();
        // reset() also rotates the idempotency key: the next operation must
        // not replay this one's cached result.
        reset();
      } else {
        // Requires pool adjustment: open preflight modal
        setState({
          step: "preflight",
          userId,
          targetPriceId,
          preview: data,
          isLoading: false,
        });
      }
    } catch (error) {
      showLicenseError(error);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  /**
   * Releases a seat back to the team's stock. Only admin-managed (free) teams
   * accept this: a paid seat is released through the quantity-reduction flow.
   */
  const unassign = async (userId: string) => {
    setState((prev) => ({ ...prev, isLoading: true, userId }));

    try {
      await api.post(`/my/team/user/${userId}/license/unassign`);
      await apiFetchUsers({ force: true });
      if (onInventoryRefetch) await onInventoryRefetch();
      reset();
    } catch (error) {
      showLicenseError(error);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const advanceToConfirm = () => {
    setState((prev) => ({ ...prev, step: "confirm" }));
  };

  const goBackToPreflight = () => {
    setState((prev) => ({ ...prev, step: "preflight" }));
  };

  const confirmChange = async () => {
    if (!state.userId || !state.targetPriceId) return;

    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      await api.post(
        `/my/team/user/${state.userId}/license/change`,
        { targetPriceId: state.targetPriceId, confirmed: true },
        { headers: { "Idempotency-Key": idempotencyKeyRef.current } },
      );
      await apiFetchUsers({ force: true });
      if (onInventoryRefetch) await onInventoryRefetch();
      reset();
    } catch (error) {
      showLicenseError(error);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  return {
    step: state.step,
    preview: state.preview,
    isLoading: state.isLoading,
    initiateChange,
    unassign,
    advanceToConfirm,
    goBackToPreflight,
    confirmChange,
    reset,
  };
};
